import ContentBodyWrapper from 'components/custom/ContentBodyWrapper'
import PaperWrapper from 'components/custom/PaperWrapper'
import { FC, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  eAdditionalDatabaseSelection,
  eBillingPeriod,
  ePackageSelection,
  ePricingMode,
} from 'components/types'
import { PackageOffering } from './components/packageOffering'
import { PackageOfferingPrice } from './components/packageOffering/packageOfferingPrice'
import { PackageOfferingFeatures } from './components/packageOffering/packageOfferingFeatures'
import { PackageOfferingAction } from './components/packageOffering/packageOfferingAction'
import {
  fallbackFunctionForUpgradeScenario,
  getOfferingsByType,
} from 'config/offerings'
import { iOffering } from 'store/types/billingPricing'
import { useDispatch, useSelector } from 'react-redux'
import { organizationProfileState } from 'store/slices/organizationProfile'
import { iOrganizationData } from 'store/types'
import { eProductKey } from 'store/types/subscriptionOrder'
import { userReducerState } from 'store/slices/user'
import { sagaGetOrganizationProfileDataAction } from 'saga/actions/user'
import { resetState } from 'store/slices/subscriptionOrderSlice'
import {
  getAddOnsDatabasesFromSubscriptions,
  getPackageProductKeyFromSubscriptions,
  isBillingRestricted,
} from '../helpers'
import { packageSelectionToPackageKey } from '../OrderForm/helpers'
import { RoutePaths } from 'routes/paths'
import { useNavigate } from 'react-router-dom'
import { FeaturesComparisonTable } from 'components/FeatureComparisonTable'

interface iPricingProps {
  alternativeTitle?: string
}

export const Pricing: FC<iPricingProps> = ({ alternativeTitle }) => {
  const { t } = useTranslation()
  let navigate = useNavigate()

  /** Organization */
  const dispatch = useDispatch()
  const { organization, roles } = useSelector(userReducerState)
  useEffect(() => {
    organization && dispatch(sagaGetOrganizationProfileDataAction())
  }, [organization, dispatch])
  const organizationData: iOrganizationData = useSelector(
    organizationProfileState
  )

  const [pricingMode, setPricingMode] = useState<ePricingMode>(
    ePricingMode.COMMERCIALS
  )
  const [billingPeriod, setBillingPeriod] = useState<eBillingPeriod>(
    eBillingPeriod.YEARLY
  )

  const [isOrderFormOpen, setIsOrderFormOpen] = useState<boolean>(false)
  // const [isUpgradeFormOpen, setIsUpgradeFormOpen] = useState<boolean>(false)
  const [packageSelection, setPackageSelection] = useState<ePackageSelection>(
    ePackageSelection.FLEX
  )
  const [additionalDatabaseSelection, setAdditionalDatabaseSelection] =
    useState<eAdditionalDatabaseSelection | undefined>()

  const [isOfferingReady, setIsOfferingReady] = useState<boolean>(false)

  const getOfferingStringFromLabel = (label: string) =>
    label.replace(/\.[a-zA-Z]+$/, '')

  const getPackageSelectionByOfferingString = (
    offeringString: string
  ): ePackageSelection => {
    if (offeringString.endsWith('.standard')) {
      return ePackageSelection.STANDARD
    } else if (offeringString.endsWith('.pro')) {
      return ePackageSelection.PRO
    } else {
      return ePackageSelection.FLEX
    }
  }

  const setPackageSelectionFromPackageKey = (key?: eProductKey) => {
    switch (key) {
      case eProductKey.SUST_STANDARD_PACKAGE:
        setPackageSelection(ePackageSelection.STANDARD)
        break
      case eProductKey.SUST_PRO_PACKAGE:
        setPackageSelection(ePackageSelection.PRO)
        break
      default:
        setPackageSelection(ePackageSelection.FLEX)
    }
  }

  /** Set Organization Package Selection */
  useEffect(() => {
    const subscriptions = Object.keys(organizationData).includes(
      'subscriptions'
    )
      ? organizationData.subscriptions
      : undefined
    setIsOfferingReady(true)
    if (!subscriptions) return

    const packageKey = getPackageProductKeyFromSubscriptions(subscriptions)
    setPackageSelectionFromPackageKey(packageKey)
  }, [organizationData, isOfferingReady])

  const handleActionClick = useCallback(
    (offering: iOffering) => {
      if (!isBillingRestricted(roles)?.length && !!organizationData?.id) {
        navigate(RoutePaths.memberAccessForbidden)
        return
      }

      const subscriptions = Object.keys(organizationData).includes(
        'subscriptions'
      )
        ? organizationData.subscriptions
        : undefined

      setAdditionalDatabaseSelection(undefined)
      const offeringString = getOfferingStringFromLabel(offering.label)

      if (
        Object.values<any>(eAdditionalDatabaseSelection).includes(
          offeringString
        )
      ) {
        setAdditionalDatabaseSelection(
          offeringString as eAdditionalDatabaseSelection
        )
      }

      const numOfActiveSubscriptions = subscriptions
        ? subscriptions?.filter((s: any) => s.active && !s.canceled).length
        : 0

      const subscriptionKey =
        getPackageProductKeyFromSubscriptions(subscriptions)

      if (numOfActiveSubscriptions > 0) {
        setPackageSelectionFromPackageKey(subscriptionKey)
      } else {
        const userPackageSelection =
          getPackageSelectionByOfferingString(offeringString)
        setPackageSelection(userPackageSelection)
      }

      if (offering.action === 'offerings.upgrade') {
        fallbackFunctionForUpgradeScenario()
      } else {
        // setIsUpgradeFormOpen(true)
        setIsOrderFormOpen(true)
      }
    },
    [organizationData, roles, t, navigate]
  )

  const handleCloseModal = () => {
    setIsOrderFormOpen(false)
    dispatch(resetState())
  }

  const renderOffering = useCallback(
    (o: iOffering, i: number, isGeneric: boolean = false) => {
      const offeringSelection: ePackageSelection = getOfferingStringFromLabel(
        o.label
      ) as ePackageSelection
      const offeringsKey = packageSelectionToPackageKey(offeringSelection)
      const organizationSelection = getPackageProductKeyFromSubscriptions(
        organizationData.subscriptions
      )
      const includedProductKeys = getAddOnsDatabasesFromSubscriptions(
        organizationData.subscriptions
      )

      if (
        o.addOnDatabaseKey &&
        includedProductKeys.includes(o.addOnDatabaseKey)
      ) {
        o.action = 'offerings.addMore'
      }

      if (!!organizationSelection && offeringsKey === organizationSelection) {
        o.action = 'offerings.addMore'
      } else if (
        organizationSelection === eProductKey.SUST_STANDARD_PACKAGE &&
        offeringSelection === ePackageSelection.PRO
      ) {
        o.action = 'offerings.upgrade'
      } else if (
        organizationSelection === eProductKey.SUST_STANDARD_PACKAGE &&
        offeringSelection === ePackageSelection.FLEX
      ) {
        o.action = 'offerings.downgrade'
        o.isActionDisabled = true
      } else if (
        organizationSelection === eProductKey.SUST_PRO_PACKAGE &&
        (offeringSelection === ePackageSelection.STANDARD ||
          offeringSelection === ePackageSelection.FLEX)
      ) {
        o.action = 'offerings.downgrade'
        o.isActionDisabled = true
      }

      const children = [
        !isGeneric && (
          <PackageOfferingPrice
            key="price"
            prefix={o?.price?.prefix}
            pricing={o?.price?.pricing}
            suffix={o?.price?.suffix}
            billingPeriod={billingPeriod}
            mode={pricingMode}
            licenseNoteKey={
              o.label.includes('pro')
                ? 'pro'
                : o.label.includes('enterprise')
                ? 'enterprise'
                : 'default'
            }
          />
        ),
        <PackageOfferingFeatures
          key="features"
          features={o.features}
          descriptionHeading={o.descriptionHeading}
          description={o.description}
          isMultipleColumns={o.isFeaturesMultipleColumns}
        />,
        <PackageOfferingAction
          key="action"
          disabled={o.isActionDisabled}
          color={o.actionColor}
          label={o.action}
          action={o.actionFunction || (() => handleActionClick(o))}
        />,
      ].filter(Boolean) as JSX.Element[]

      return (
        <PackageOffering
          key={i}
          title={o.label}
          elevated={o.isElevated}
          labelColor={o.labelColor}
          description={o.description}
          labelIcon={o.labelIcon}
          inclPrice={!isGeneric} // 👈 Adjust here
        >
          {children}
        </PackageOffering>
      )
    },
    [organizationData, billingPeriod, pricingMode, handleActionClick]
  )
  return (
    <>
      <ContentBodyWrapper title={t(alternativeTitle || `pages.pricing`)}>
        <PaperWrapper>
          {/* Pricing grid - Fixed responsive grid classes */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full px-2 sm:px-4 lg:px-6">
            {Object.values(getOfferingsByType(pricingMode)).map((o, i) => (
              <div key={i} className="mt-6 w-full">
                {renderOffering(o, i)}
              </div>
            ))}
          </div>

          {/* Add-ons grid - Fixed responsive grid classes */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full mt-8 px-2 sm:px-4 lg:px-6">
            {Object.values(getOfferingsByType('generic')).map((o, i) => (
              <div key={i} className="w-full">
                {renderOffering(o, i, true)} {/* true = isGeneric */}
              </div>
            ))}
          </div>

          {/* Features Table */}
          <div className="w-full max-w-[1600px] mx-auto px-2 sm:px-4 lg:px-6">
            <FeaturesComparisonTable />
          </div>
          <p className="italic mt-4 ">*{t('offerings.netPriceNote')}</p>
        </PaperWrapper>
      </ContentBodyWrapper>
    </>
  )
}
